import React from 'react';

import { cn } from '@cobliteam/ui/utils';
import * as styles from './styles';

import type { DateRange, UUID, Vehicle } from '@cobliteam/common-types';

import { Analytics } from '@cobliteam/analytics';

import { Chip, type OptionKey } from '@cobliteam/base-components';

import { MultiSelectVehicles } from '@cobliteam/ui-business/components';

import Icon from '@cobliteam/ui/icons';

import Typography from '@cobliteam/ui/components/Typography';

import { TimeFilter } from '../../../TimeFilter/TimeFilter';

export interface FiltersSectionProps {
  vehicles: Vehicle[];
  wantedVehicleIds: UUID[];
  wantedTimeSelection: DateRange;
  onVehicleFilterChanged: (vehicleIds: UUID[]) => void;
  onTimeFilterChanged: (timeSelection: DateRange) => void;
}

enum FilterOption {
  VEHICLE = 'VEHICLE',
  TIME = 'TIME',
}

export const FiltersSection: React.FC<FiltersSectionProps> = ({ vehicles, wantedVehicleIds, wantedTimeSelection, ...props }) => {
  const [filterOpened, setFilterOpened] = React.useState<FilterOption | undefined>(undefined);

  const getVehiclesCheckboxOptions = () => {
    const allVehicles = new Map<UUID, string>();

    vehicles.forEach((vehicle) => allVehicles.set(vehicle.id, vehicle.label));
    return Array.from(allVehicles.entries()).map(([vehicleId, label]) => ({
      key: vehicleId,
      label,
    }));
  };

  const handleVehicleFilterApply = (selectedVehicles: Vehicle[]) => {
    const vehicleIds = selectedVehicles.map((vehicle) => vehicle.id);
    setFilterOpened(undefined);
    props.onVehicleFilterChanged(vehicleIds);
  };

  const handleTimeFilterApply = (timeSelection: DateRange) => {
    setFilterOpened(undefined);
    props.onTimeFilterChanged(timeSelection);
  };

  const handleClearFilter = (filterType: string) => {
    Analytics.track(`Tracking History ${filterType} Filter Cleared`);
  };

  const handleVehicleChipDelete = (deletedVehicleId: OptionKey) => {
    const newWantedVehicleIds = wantedVehicleIds.filter((vehicleId) => vehicleId !== deletedVehicleId);
    props.onVehicleFilterChanged(newWantedVehicleIds);
  };

  const vehiclesOptions = getVehiclesCheckboxOptions();

  return (
    <>
      <div className='mb-2.5 flex items-center gap-12'>
        <div className={cn(styles.filtersSection, 'flex gap-5')}>
          <MultiSelectVehicles
            selectedVehicles={vehicles.filter((v) => wantedVehicleIds.includes(v.id))}
            onChange={handleVehicleFilterApply}
            onClear={() => handleClearFilter('Vehicles')}
          />
          <TimeFilter
            timeSelection={wantedTimeSelection}
            isOpen={filterOpened === FilterOption.TIME}
            onApply={handleTimeFilterApply}
            onClear={() => handleClearFilter('Hours')}
            onDropdownClick={() => setFilterOpened(FilterOption.TIME)}
            onCancel={() => setFilterOpened(undefined)}
          />
        </div>
        <div className='flex flex-wrap items-center gap-2.5'>
          <div className='flex items-center gap-1'>
            <Icon name='square' className='fill-gray-9 text-gray' />
            <Typography variant='caption-default'>Paradas</Typography>
          </div>
          <div className='flex items-center gap-1'>
            <Icon name='square' className='fill-gray-4 text-gray-4' />
            <Typography variant='caption-default'>Período de circulação do veículo</Typography>
          </div>
          <div className='flex items-center gap-1'>
            <Icon name='square' className='fill-warning-9 text-warning-9' />
            <Typography variant='caption-default'>Dispositivo desconectado</Typography>
          </div>
          <div className='flex items-center gap-1'>
            <Icon name='square' className='fill-success-9 text-success-9' />
            <Typography variant='caption-default'>Dispositivo conectado</Typography>
          </div>
        </div>
      </div>
      {Boolean(wantedVehicleIds.length) && (
        <div className={styles.chipsWrapper}>
          <Typography variant='input-label'>Visualizando:</Typography>
          {wantedVehicleIds.map((vehicleId, index) => {
            const chipOption = vehiclesOptions.find(({ key }) => key === vehicleId);
            const chipLabel = chipOption?.label;
            return <Chip key={index} label={chipLabel} onDelete={() => handleVehicleChipDelete(vehicleId)} />;
          })}
        </div>
      )}
    </>
  );
};
